import { CreateLegalTermsRequest, UpdateLegalTermsRequest } from "@givenwell/management-api";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { api, queryClient } from "./utils";

type ListParams = {
  page: number;
  pageSize: number;
  audience?: "supplier" | "supporter" | null;
  appliesWhenIntent?: "marketplace" | "eap" | null;
  countryCode?: string;
  orgId?: string;
};

export const legalTermsQueryKey = ["legal-terms"];

export const listLegalTermsQuery = (params: ListParams) =>
  queryOptions({
    queryKey: [...legalTermsQueryKey, params],
    queryFn: () => api.legalTerms.listLegalTerms(params),
  });

export const legalTermsQuery = (termsId: string) =>
  queryOptions({
    queryKey: [...legalTermsQueryKey, termsId],
    queryFn: () => api.legalTerms.getLegalTerms({ termsId }),
  });

export function useGetUploadUrlMutation() {
  return useMutation({
    mutationFn: () => api.legalTerms.getLegalTermsUploadUrl(),
  });
}

export function useUploadMarkdownMutation() {
  return useMutation({
    mutationFn: async ({ uploadUrl, markdown }: { uploadUrl: string; markdown: string }) => {
      const response = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": "text/markdown" },
        body: markdown,
      });
      if (!response.ok) {
        throw new Error(`Failed to upload markdown: ${response.status} ${response.statusText}`);
      }
    },
  });
}

export function useCreateLegalTermsMutation() {
  return useMutation({
    mutationFn: (requestBody: CreateLegalTermsRequest) => api.legalTerms.createLegalTerms({ requestBody }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsQueryKey });
    },
  });
}

export function useActivateLegalTermsMutation() {
  return useMutation({
    mutationFn: (termsId: string) => api.legalTerms.activateLegalTerms({ termsId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsQueryKey });
    },
  });
}

export function useUpdateLegalTermsMutation() {
  return useMutation({
    mutationFn: ({ termsId, requestBody }: { termsId: string; requestBody: UpdateLegalTermsRequest }) =>
      api.legalTerms.updateLegalTerms({ termsId, requestBody }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsQueryKey });
    },
  });
}

export function useDeleteLegalTermsMutation() {
  return useMutation({
    mutationFn: (termsId: string) => api.legalTerms.deleteLegalTerms({ termsId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: legalTermsQueryKey });
    },
  });
}
